// import components
import { Tooltip } from 'components/shared';
import { CustomDataGridAction } from 'components/shared/DataGrid/components/customDataGridAction';
import { YearClosureProgress } from '../components/YearClosureProgress';
import { GridColDef } from '@mui/x-data-grid-pro';

// import external libraries
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import hooks
import { useSelector } from 'hooks';

// import styles
import { StyledBoxFlexAlignCenter } from 'components/shared/Box/Box.styles';
import {
  StyledOpenedStatusBox,
  StyledUnrealizedDutyCountBox
} from './useGetColumnDefs.styles';

// import types
import { UseGetColumnDefsParams } from './useGetColumnDefs.types';

// import utils
import { isNil } from 'ramda';
import { Icon } from '@sdb/planning-ui';

const hasFinishedAll = (currentYearProgress, nextYearProgress) => {
  if (!currentYearProgress || !nextYearProgress) {
    return true;
  }
  return !!(currentYearProgress.hasFinished && nextYearProgress.hasFinished);
};

export const useGetColumnDefs = (
  params: UseGetColumnDefsParams
): GridColDef[] => {
  const {
    openYear,
    closeYear,
    onCurrentYearErrorClick,
    onNextYearErrorClick,
    currentYearProgress,
    nextYearProgress,
    currentYearErrors,
    nextYearErrors
  } = params;
  const { t } = useTranslation('common');
  const [isActionHidden, setIsActionHidden] = useState(false);
  const { hasAssignOrgGrants } = useSelector(({ grants }) => grants);

  useEffect(() => {
    setIsActionHidden(!hasFinishedAll(currentYearProgress, nextYearProgress));
  }, [currentYearProgress, nextYearProgress]);

  const openHandler = (id, row) => {
    openYear?.(id, row);
  };

  const closeHandler = (id, row) => {
    closeYear?.(id, row);
  };

  const hasGrantsToOpenYear = () => {
    return hasAssignOrgGrants;
  };

  return useMemo(
    () => [
      {
        field: 'year',
        headerName: t('year'),
        type: 'number',
        valueFormatter: (value) => value,
        headerAlign: 'left',
        align: 'left',
        minWidth: 100
      },
      {
        field: 'isOpenedStatus',
        headerName: t('status'),
        type: 'boolean',
        filterable: false,
        renderCell: ({ value }) => (
          <>
            {value
              ? t('yearClosure.openedStatus')
              : t('yearClosure.closedStatus')}
          </>
        ),
        headerAlign: 'center',
        align: 'center',
        minWidth: 100
      },
      {
        field: 'unrealizedDuties',
        headerName: t('yearClosure.unrealized_duty_count'),
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        valueFormatter: (value) => value,
        renderHeader: () => (
          <StyledBoxFlexAlignCenter>
            <Tooltip
              title={t('yearClosure.unrealized_duty_count_info_icon_text')}
            >
              <Icon type="circle-exclamation" color="warning" />
            </Tooltip>
            <StyledUnrealizedDutyCountBox>
              {t('yearClosure.unrealized_duty_count')}
            </StyledUnrealizedDutyCountBox>
          </StyledBoxFlexAlignCenter>
        )
      },
      {
        field: 'customActions',
        headerName: t('actions'),
        flex: 1,
        renderCell: (params) => (
          <>
            {params.row.isOpenedStatus ? (
              isActionHidden ? null : (
                <CustomDataGridAction
                  action={{
                    icon: 'lock',
                    onClick: (id, row) => closeHandler(id, row),
                    tooltipTitle: t('yearClosure.close_year'),
                    disabled: true
                  }}
                  key="openYear"
                  params={params}
                />
              )
            ) : hasGrantsToOpenYear() ? (
              isActionHidden ? null : (
                <CustomDataGridAction
                  action={{
                    icon: 'lock-open',
                    onClick: (id, row) => openHandler(id, row),
                    tooltipTitle: t('yearClosure.open_year'),
                    disabled: isActionHidden
                  }}
                  key="closeYear"
                  params={params}
                />
              )
            ) : null}
            {params.row.isOpenedStatus ? (
              <StyledOpenedStatusBox>
                {!isNil(currentYearProgress) || !isNil(nextYearProgress) ? (
                  <YearClosureProgress
                    onErrorClick={onCurrentYearErrorClick}
                    progress={{
                      hasFinished: true,
                      dutyLinesProgress: 100,
                      dutyLinesTotal: 100,
                      errorDetails: [],
                      dutyLinesErrors: 0
                    }}
                    errors={currentYearErrors}
                    title={t('yearClosure.close_current_year')}
                  />
                ) : null}
                {!isNil(currentYearProgress) ? (
                  <YearClosureProgress
                    onErrorClick={onCurrentYearErrorClick}
                    progress={currentYearProgress}
                    errors={currentYearErrors}
                    title={t('yearClosure.calculate_current_year')}
                  />
                ) : null}
                {!isNil(nextYearProgress) ? (
                  <YearClosureProgress
                    onErrorClick={onNextYearErrorClick}
                    progress={nextYearProgress}
                    errors={nextYearErrors}
                    title={t('yearClosure.calculate_next_year')}
                  />
                ) : null}
              </StyledOpenedStatusBox>
            ) : null}
          </>
        )
      }
    ],
    [
      t,
      openYear,
      closeYear,
      isActionHidden,
      nextYearProgress,
      currentYearProgress,
      currentYearErrors,
      nextYearErrors
    ]
  );
};
